import {
  DataType,
  getHashString,
  ICTShapeProperties,
  ICTTextParagraph,
  IPictureData,
  IPresentationData,
  IShapeData,
  ISlideData,
} from '@tencent/slide-sdk-model';
import { IMAGE_MIME_TYPE } from '@tencent/slide-sdk-model/src/constants/media-constants';
import { IImageResourceData } from '@tencent/slide-sdk-model/src/data/resource-map-data';
import { getExtnameFromUrl, getImageSizeFromUrl } from '@tencent/slide-sdk-model/src/utils/url-utils';
import { cloneDeep, sample } from 'lodash';
import { marked } from 'marked';
import { ShapeTag, ShapeType, SlideType } from './define';
import { generator, ITemplate } from './generator';
import { Struct, StructBase, StructWithList } from './parse-page';
import { renderParagraphList } from './renderer';
import { getShapeTag } from './schema';
import { getSerialFormatter, isSerial } from './serial-number';
import { canShapeContainsText } from './text-measure/text-measure-light';
import {
  deleteShapeByIdInSlide,
  deleteShapeInShapeList,
  getShapeByIdInSlide,
  getShapeInSlide,
  getShapesInShapeList,
  getShapeTextRun,
  traverseShapeList,
} from './utils/sdk-data-utils';
import { getTextOfToken } from './utils/token-utils';
import { uuid } from './utils/utils';

function updateResourceMap(url: string, resourceMap?: IPresentationData['resourceMap']): IImageResourceData {
  if (resourceMap) {
    const res = Object.values(resourceMap.images).find((imgRes) => imgRes.src === url);
    if (res) return res;
  }

  const imageDescriptor = createImageResourceData(url);

  if (resourceMap) {
    resourceMap.images[imageDescriptor.id] = imageDescriptor;
  }

  return imageDescriptor;
}

function createImageResourceData(url: string): IImageResourceData {
  const ext = getExtnameFromUrl(url);
  const id = `rel:${getHashString(url)}`;
  // 未识别到的 https 资源以图片 jpeg 格式处理
  const imageMimeType = IMAGE_MIME_TYPE.get(ext) ?? 'image/jpeg';
  const imageSize = getImageSizeFromUrl(url);
  const imageDescriptor = {
    id,
    src: url,
    type: imageMimeType,
    ...imageSize,
  };

  return imageDescriptor;
}

interface Options {
  shouldLog?: boolean;
  lengthMargin?: number;
  ignoreImage?: boolean;
}

export function findCandidates(
  page: StructWithList,
  candidates: ITemplate[],
  type: SlideType,
  options?: Options,
): ITemplate[] {
  const shouldLog = options?.shouldLog ?? false;
  const lengthMargin = options?.lengthMargin ?? 0;
  const ignoreImage = options?.ignoreImage ?? false;

  const candidate = candidates.filter((template) => {
    function validate(
      template: ITemplate,
      schema: any,
      idMap: any,
      page: StructBase | StructWithList,
      path = '',
      ignoreImage = true,
    ): [boolean, Object] {
      // title
      if (page.title) {
        if (schema.properties.title) {
          // if (!ignoreTextMeasure && !(maxLength > length && minLength < length)) {
          const ignoreTextMeasure = path === '';
          if (!ignoreTextMeasure) {
            const shapeData = getShapeByIdInSlide(template.slideData, idMap.title) as IShapeData;
            if (!shapeData) {
              console.error('[unexpected] shape not found');
            } else {
              const { result } = canShapeContainsText(shapeData, getTextOfToken(page.title));
              if (!result) {
                const error = {
                  path: `/${path}title`,
                  schema: schema.properties.title,
                  message: `length not match`,
                };
                return [false, error];
              }
            }
          }
        } else {
          if (path === '') {
            // 页标题，支持为空。使用兜底标题组件
          } else {
            const error = {
              path: `/${path}title`,
              message: 'missing title filed',
            };
            return [false, error];
          }
        }
      } else {
        if (schema.properties.title) {
          const error = {
            path: `/${path}title`,
            schema: schema.properties.title,
            message: '"title" is required',
          };
          return [false, error];
        } else {
          // ok
        }
      }

      // content
      if (page.content) {
        if (schema.properties.content) {
          const shapeData = getShapeByIdInSlide(template.slideData, idMap.content) as IShapeData;
          if (!shapeData) {
            console.error('[unexpected] shape not found');
          } else {
            const text = page.content
              .map((tk) => {
                if ('text' in tk) return tk.text;
                return tk.raw;
              })
              .join('\n');
            const { result } = canShapeContainsText(shapeData, text);
            if (!result) {
              const error = {
                path: `/${path}content`,
                schema: schema.properties.title,
                message: `length not match`,
              };
              return [false, error];
            }
          }
        } else {
          const error = {
            path: `/${path}content`,
            message: 'missing content filed',
          };
          return [false, error];
        }
      } else {
        if (schema.properties.content) {
          let { canOmit = false } = schema.properties.content;
          // TODO:
          if (type !== SlideType.page) canOmit = true;
          if (!canOmit && !page.content) {
            const error = {
              path: `/${path}content`,
              schema: schema.properties.content,
              message: '"content" is required',
            };
            return [false, error];
          }
        } else {
          // ok
        }
      }

      if (page.image) {
        if (schema.properties.image) {
          // TODO: 尺寸校验
        } else {
          if (ignoreImage) {
            // pass
            ignoreImage = false;
          } else {
            return [
              false,
              {
                path: `/${path}image`,
                schema: schema.properties.image,
                message: 'missing "image" filed',
              },
            ];
          }
        }
      } else {
        if (schema.properties.image) {
          return [
            false,
            {
              path: `/${path}image`,
              schema: schema.properties.image,
              message: '"image" is required',
            },
          ];
        } else {
          // ok
        }
      }

      if ('list' in page && page.list) {
        if (schema.properties.list) {
          const { maxLength = Number.POSITIVE_INFINITY, minLength = 0, items } = schema.properties.list;
          const { length } = page.list;

          if (length < minLength || length - maxLength > lengthMargin) {
            const error = {
              path: `/${path}list`,
              schema: schema.properties.list,
              message: `length not match, [${minLength}, ${maxLength}] ${length}`,
            };
            return [false, error];
          }

          for (let index = 0; index < page.list.length; index++) {
            const listItem = page.list[index];
            const idmap = idMap.list[index];
            if (!idmap) continue;

            const res = validate(template, items, idmap, listItem, 'list/', ignoreImage);
            if (!res[0]) return res;
          }
        } else {
          const error = {
            path: `/${path}list`,
            message: 'missing list filed',
          };
          return [false, error];
        }
      } else {
        if (schema.properties.list) {
          const error = {
            path: `/${path}list`,
            schema: schema.properties.list,
            message: '"list" is required',
          };
          return [false, error];
        } else {
          // ok
        }
      }

      return [true, {}];
    }

    const { schema, idMap } = template.tag;
    const res = validate(template, schema, idMap, page, '', ignoreImage);
    if (!res[0] && shouldLog) {
      // console.debug('validate failed', res[1]);
    }
    return res[0];
  });
  return candidate;
}

export function generateSlideFromTemplate(
  page: StructWithList,
  type: SlideType,
  template: ITemplate,
  layoutId: string,
  resourceMap?: IPresentationData['resourceMap'],
): ISlideData[] {
  // TODO: 内容分割
  let splitShapeKey;
  let splitParagraphList;

  const slide = cloneDeep(template.slideData);
  const originSlideId = slide.id;
  slide.id = uuid();
  if (!template.fromSamePresentation) {
    slide.layoutId = layoutId;
  }

  if (slide.cSld.spTree) {
    if (slide.cSld.spTree.nvPr.descr) {
      slide.cSld.spTree.nvPr.descr = JSON.stringify({
        ...JSON.parse(slide.cSld.spTree.nvPr.descr),
        originSlideId,
      });
    } else {
      const slideTag = {
        isTemplate: true,
        type,
        originSlideId,
      };
      slide.cSld.spTree.nvPr.descr = JSON.stringify(slideTag);
    }
  }

  const tag = template.tag;
  const { idMap, schema } = tag;

  if (type === SlideType.session) {
    traverseShapeList(slide.cSld.spTree!.shapeList, (shape) => {
      if (shape.type !== DataType.Shape) return false;

      const text = getShapeTextRun(shape);
      const formatter = getSerialFormatter(text);
      if (!formatter) return;

      const serialText = formatter(generator.context.sessionCount - 1);

      // TODO:
      const plist = shape.txBody!.p! as ICTTextParagraph[];
      const firstRun = plist[0].egTextRun![0];
      // TODO:
      firstRun.r!.t = serialText;
      plist[0].egTextRun = [plist[0].egTextRun![0]];
      shape.txBody!.p! = [plist[0]];
    });
  }

  let hasTitleInserted = false;

  Object.keys(idMap).forEach((key) => {
    if (key === ShapeType.title || key === ShapeType.content || key === ShapeType.image) {
      const id = idMap[key];
      if (!id) return;

      const shape = getShapeByIdInSlide(slide, id);
      if (!shape) return;

      if (key === ShapeType.title || key === ShapeType.content) {
        if (shape.type !== DataType.Shape) {
          console.error('[unexpected] --');
          return;
        }

        const token = page[key];
        const { txBody, spPr } = shape;
        // TODO:
        // const { paragraphProps, textRunProps } = extractPropsFromPara(shape.textBody.paragraphList);
        if (token) {
          const paragraphList = renderParagraphList(token, txBody?.p!, key);
          // TODO: 取 tag 的方式
          // TODO: 缩放
          if (type !== SlideType.page) {
            // 页标题不缩放了
            if (key === ShapeType.title) {
              const text = (token as Struct[ShapeType.title])!.text;
              const { fontSize, expectFontSize } = canShapeContainsText(shape, text, true);
              if (fontSize > expectFontSize) {
                (paragraphList as any)[0].egTextRun[0].r.rPr.sz = expectFontSize * 100;
              }
            }
          }

          shape.txBody!.p = paragraphList;
          if (!shape.nvPr.descr) {
            const shapeTag: ShapeTag = {
              isTemplate: true,
              type: key,
              canOmit: false,
            };
            shape.nvPr = {
              ...shape.nvPr,
              descr: JSON.stringify(shapeTag),
            };
          }
          if (key === ShapeType.title) hasTitleInserted = true;

          // TODO:
          // const paragraphLists = cutParagraphList(paragraphList, spPr);
          // if (paragraphLists.length === 1) {
          //     shape.api.updateParagraphList(paragraphLists[0]);
          // } else {
          //     const p = paragraphLists.shift();
          //     splitShapeKey = key;
          //     splitParagraphList = paragraphLists;
          //     shape.api.updateParagraphList(p!);
          // }
          // shape.api.updateTextBodyProperties({
          //     bodyPr: {
          //         normAutofit: {
          //             fontScale: 100000
          //         },
          //         spAutoFit: null,
          //         noAutofit: null,
          //         wrap: 2
          //     }
          // });
        } else {
          deleteShapeByIdInSlide(slide, shape.id);
        }
      } else if (key === ShapeType.image) {
        const imageToken = page[key];
        if (!imageToken) return;

        // 兜底图
        // const url = v ?? sample(resource)!;
        if (shape.type !== DataType.Picture && shape.type !== DataType.Shape) {
          console.error('[unexpected] --');
          return;
        }
        changePictureInShape(imageToken, shape, resourceMap);
        if (!shape.nvPr.descr) {
          const shapeTag: ShapeTag = {
            isTemplate: true,
            type: key,
            canOmit: false,
          };
          shape.nvPr = {
            ...shape.nvPr,
            descr: JSON.stringify(shapeTag),
          };
        }
      }

      return;
    }

    if (key === ShapeType.list) {
      const idmaps = idMap[key];
      if (!Array.isArray(idmaps)) return;

      const structList = page[key];
      if (!structList) return;

      // TODO: list 没有设计存储 id，出此下策
      const listShape = getShapeInSlide(slide, (s) => {
        const tag = getShapeTag(s);
        if (!tag?.isTemplate) return false;

        return tag.type === ShapeType.list;
      });
      if (listShape?.type !== DataType.GroupShape) return;

      if (listShape.shapeList.length > structList.length) {
        const ext1 = { ...listShape.spPr.xfrm?.ext };
        // const off1 = { ...listShape.props.spPr.xfrm?.off };
        listShape.shapeList.slice(structList.length).forEach((shape) => {
          deleteShapeInShapeList(listShape.shapeList, shape.id);
        });
        const ext2 = { ...listShape.spPr.xfrm?.ext };
        const off2 = { ...listShape.spPr.xfrm?.off };

        const tag = getShapeTag(listShape)!;
        if (tag.isTemplate && tag.type === ShapeType.list) {
          if (tag.scalable) {
            listShape.spPr.xfrm = {
              ...listShape.spPr.xfrm,
              ext: ext1,
            };
          } else {
            const deltaW = ext1.cx! - ext2.cx!;
            const deltaH = ext1.cy! - ext2.cy!;
            switch (tag.alignment) {
              case 'left':
                break;
              case 'center':
                off2.x = off2.x! + deltaW / 2;
                break;
              case 'right':
                off2.x = off2.x! + deltaW;
                break;
            }

            switch (tag.alignmentVertical) {
              case 'top':
                break;
              case 'center':
                off2.y = off2.y! + deltaH / 2;
                break;
              case 'bottom':
                off2.y = off2.y! + deltaH;
                break;
            }
          }
          listShape.spPr.xfrm = {
            ...listShape.spPr.xfrm,
            off: off2,
          };
        }
      }

      if (!listShape.nvPr.descr) {
        const shapeTag: ShapeTag = {
          isTemplate: true,
          type: key,
          alignment: 'left',
          alignmentVertical: 'top',
          canOmit: false,
          scalable: false,
          minItemsCount: -1,
        };
        listShape.nvPr = {
          ...listShape.nvPr,
          descr: JSON.stringify(shapeTag),
        };
      }

      const titleShapes: IShapeData[] = [];
      const contentShapes: IShapeData[] = [];
      let titleFontSize = 0;
      let contentFontSize = 0;
      idmaps.forEach((idmap, idx) => {
        const struct = structList[idx];
        if (!struct) return;

        Object.keys(idmap).forEach((key) => {
          if (key === ShapeType.title || key === ShapeType.content || key === ShapeType.image) {
            const id = idmap[key];
            if (!id) return;

            const shape = getShapeByIdInSlide(slide, id);
            if (!shape) return;

            if (key === ShapeType.title || key === ShapeType.content) {
              if (shape.type !== DataType.Shape) {
                console.error('[unexpected] --');
                return;
              }

              const token = struct[key];

              if (token) {
                if (key === ShapeType.title) {
                  titleShapes.push(shape);
                } else {
                  contentShapes.push(shape);
                }

                let text = '';
                if (Array.isArray(token)) {
                  text = token
                    .map((tk) => {
                      if ('text' in tk) return tk.text;
                      return tk.raw;
                    })
                    .join('\n');
                } else {
                  text = getTextOfToken(token);
                }
                const { fontSize, expectFontSize } = canShapeContainsText(shape, text, true);
                if (fontSize > expectFontSize) {
                  if (key === ShapeType.title) {
                    titleFontSize = expectFontSize;
                  } else {
                    contentFontSize = expectFontSize;
                  }
                }
                const paragraphList = renderParagraphList(token, shape.txBody?.p!, key);
                shape.txBody!.p = paragraphList;
                if (!shape.nvPr.descr) {
                  const shapeTag: ShapeTag = {
                    isTemplate: true,
                    type: key,
                    canOmit: false,
                  };
                  shape.nvPr = {
                    ...shape.nvPr,
                    descr: JSON.stringify(shapeTag),
                  };
                }
              } else {
                deleteShapeByIdInSlide(slide, shape.id);
              }
            } else if (key === ShapeType.image) {
              const imageToken = struct[key];
              if (!imageToken) return;

              if (shape.type !== DataType.Picture && shape.type !== DataType.Shape) {
                console.error('[unexpected] --');
                return;
              }
              changePictureInShape(imageToken, shape, resourceMap);
              if (!shape.nvPr.descr) {
                const shapeTag: ShapeTag = {
                  isTemplate: true,
                  type: key,
                  canOmit: false,
                };
                shape.nvPr = {
                  ...shape.nvPr,
                  descr: JSON.stringify(shapeTag),
                };
              }
            }

            return;
          }
        });
      });

      const updateShapeFontSize = (shape: IShapeData, fontSize: number) => {
        shape.txBody?.p?.forEach((p) =>
          p.egTextRun?.forEach((run) => {
            if (run.r) {
              run.r.rPr = {
                ...run.r.rPr,
                sz: fontSize * 100,
              };
            }
          }),
        );
      };

      if (titleFontSize > 0) {
        titleShapes.forEach((shape) => updateShapeFontSize(shape, titleFontSize));
      }
      if (contentFontSize > 0) {
        contentShapes.forEach((shape) => updateShapeFontSize(shape, contentFontSize));
      }
    }
  });

  if (page.title && !hasTitleInserted) {
    const shape = sample(generator.componentMap.title) ?? null;

    if (shape && shape.type === DataType.Shape) {
      const s = cloneDeep(shape);
      s.id = uuid();

      const paragraphList = renderParagraphList(page.title, s.txBody?.p!, ShapeType.title);

      s.txBody!.p = paragraphList;
      slide.cSld.spTree?.shapeList.push(s);
    }
  }

  if (splitShapeKey && splitParagraphList.length > 0) {
    splitParagraphList.forEach((paragraphList) => {
      const cloneSlideData = cloneDeep(slide);
      cloneSlideData.id = uuid();

      const shapeId = idMap[splitShapeKey];
      const shape = getShapeByIdInSlide(cloneSlideData, shapeId) as IShapeData;
      // TODO: 11
      if (shape) shape.txBody!.p = paragraphList;
    });
  }

  return [slide];
}

const changePictureInShape = (
  imageToken: marked.Tokens.Image,
  shape: IPictureData | IShapeData,
  resourceMap?: IPresentationData['resourceMap'],
) => {
  const { href, text } = imageToken;
  generator.context.imageKeyArr.push({
    shapeId: shape.id,
    keyword: text,
  });
  const resource = updateResourceMap(href, resourceMap) as any;
  // const resource = getImageSizeFromUrl(url);
  const taregetRect = shape.spPr.xfrm?.ext!;
  const targetRatio = taregetRect.cx! / taregetRect.cy!;

  const ratio = resource.width / resource.height;
  let w, h;
  if (ratio > targetRatio) {
    w = resource.height * targetRatio;
    h = resource.height;
  } else {
    w = resource.width;
    h = resource.width / targetRatio;
  }

  const x = Math.floor(((resource.width - w) / 2 / resource.width) * 100000);
  const y = Math.floor(((resource.height - h) / 2 / resource.height) * 100000);

  if (shape.type === DataType.Picture) {
    shape.blipFill = {
      ...shape.blipFill,
      blip: { embed: resource.id },
      srcRect: {
        l: x,
        t: y,
        r: x,
        b: y,
      },
      egFillModeProperties: {
        stretch: {
          fillRect: {},
        },
      },
    };
  } else {
    const rate = (taregetRect.cx! / (w * 12700)) * 100000 * 2;
    shape.spPr.blipFill = {
      ...shape.spPr.blipFill,
      blip: { embed: resource.id },
      srcRect: {
        l: x,
        t: y,
        r: x,
        b: y,
      },
      egFillModeProperties: {
        stretch: {
          fillRect: {},
        },
        tile: {
          // 平铺居中
          flip: 1,
          algn: 5,
          sx: rate,
          sy: rate,
        },
      },
    };
  }
};

function cutParagraphList(paragraphList: ICTTextParagraph[], spPr: ICTShapeProperties): ICTTextParagraph[][] {
  let result: ICTTextParagraph[][] = [];
  while (paragraphList.length > 0) {
    result.push(paragraphList.splice(0, 10));
  }

  return result;
}
